/**
 * NFT Activity Utilities
 * Functions for fetching and managing on-chain NFT activity
 */

import { supabase } from '../lib/supabase.js';

/**
 * Format timestamp to relative time (e.g., "2h ago", "1d ago")
 * @param {string|Date} timestamp - The timestamp to format
 * @returns {string} Formatted relative time
 */
function formatTimeAgo(timestamp) {
  if (!timestamp) return 'Unknown';
  
  const now = new Date();
  const time = new Date(timestamp);
  const diffMs = now - time;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return time.toLocaleDateString();
}

/**
 * Fetch recent activity for a specific mint address
 * @param {string} mintAddress - The mint address of the NFT
 * @param {number} limit - Maximum number of activities to fetch (default: 10)
 * @returns {Promise<Array>} Array of activity records
 */
export async function getMintActivity(mintAddress, limit = 10) {
  try {
    if (!mintAddress) {
      return [];
    }

    console.log(`🔍 [ACTIVITY] Fetching for mint: ${mintAddress.substring(0, 15)}...`);

    // Query the nft_activity table - try both ordering options
    let query = supabase
      .from('nft_activity')
      .select('*')
      .eq('mint_address', mintAddress);
    
    // Order by block_time if available, otherwise created_at
    query = query.order('block_time', { ascending: false, nullsFirst: false });
    query = query.order('created_at', { ascending: false });
    query = query.limit(limit);

    const { data, error } = await query;

    if (error) {
      console.error('❌ [ACTIVITY] Error:', error.message);
      return [];
    }

    console.log(`✅ [ACTIVITY] Found ${data?.length || 0} records`);
    
    if (!data || data.length === 0) {
      console.warn('⚠️ [ACTIVITY] No records found - checking if piece exists...');
      const { data: pieceCheck } = await supabase
        .from('pieces')
        .select('id, name, mint_address')
        .eq('mint_address', mintAddress)
        .maybeSingle();
      console.log('🔍 [ACTIVITY] Piece in DB:', pieceCheck ? `"${pieceCheck.name}"` : 'NOT FOUND');
    }
    
    // Format the data to match expected structure
    const formattedData = data?.map(record => {
      const timestamp = record.block_time || record.created_at;
      return {
        ...record,
        event_timestamp: timestamp ? Math.floor(new Date(timestamp).getTime() / 1000) : Math.floor(Date.now() / 1000),
        from_wallet: record.from_address || record.from_wallet,
        to_wallet: record.to_address || record.to_wallet,
        sol_amount: record.amount_lamports ? record.amount_lamports / 1e9 : (record.price || 0),
        time_ago: formatTimeAgo(timestamp),
        signature: record.tx_sig || record.signature || record.transaction_hash || 'unknown'
      };
    }) || [];

    return formattedData;
  } catch (error) {
    console.error('❌ [ACTIVITY] Exception:', error);
    return [];
  }
}

/**
 * Fetch recent activity for a specific wallet address
 * @param {string} walletAddress - The wallet address
 * @param {number} limit - Maximum number of activities to fetch (default: 20)
 * @returns {Promise<Array>} Array of activity records
 */
export async function getWalletActivity(walletAddress, limit = 20) {
  try {
    if (!walletAddress) {
      console.warn('No wallet address provided to getWalletActivity');
      return [];
    }

    console.log(`🔍 Fetching activity for wallet: ${walletAddress}`);

    // Query activities where wallet is either from_address or to_address
    let query = supabase
      .from('nft_activity')
      .select('*')
      .or(`from_address.eq.${walletAddress},to_address.eq.${walletAddress}`);
    
    // Order by block_time if available, otherwise created_at
    query = query.order('block_time', { ascending: false, nullsFirst: false });
    query = query.order('created_at', { ascending: false });
    query = query.limit(limit);

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching wallet activity:', error);
      return [];
    }

    console.log(`✅ Found ${data?.length || 0} activity records`);
    
    // Format the data to match expected structure
    const formattedData = data?.map(record => {
      const timestamp = record.block_time || record.created_at;
      return {
        ...record,
        event_timestamp: timestamp ? Math.floor(new Date(timestamp).getTime() / 1000) : Math.floor(Date.now() / 1000),
        from_wallet: record.from_address || record.from_wallet,
        to_wallet: record.to_address || record.to_wallet,
        sol_amount: record.amount_lamports ? record.amount_lamports / 1e9 : (record.price || 0),
        time_ago: formatTimeAgo(timestamp),
        signature: record.tx_sig || record.signature || record.transaction_hash || 'unknown',
        direction: (record.from_address || record.from_wallet) === walletAddress ? 'outgoing' : 'incoming'
      };
    }) || [];

    return formattedData;
  } catch (error) {
    console.error('Exception in getWalletActivity:', error);
    return [];
  }
}

/**
 * Get activity summary for a mint address
 * @param {string} mintAddress - The mint address of the NFT
 * @returns {Promise<Object|null>} Activity summary object
 */
export async function getActivitySummary(mintAddress) {
  try {
    if (!mintAddress) {
      console.warn('No mint address provided to getActivitySummary');
      return null;
    }

    const { data, error } = await supabase
      .from('activity_summary')
      .select('*')
      .eq('mint_address', mintAddress)
      .maybeSingle();

    if (error) {
      console.error('Error fetching activity summary:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Exception in getActivitySummary:', error);
    return null;
  }
}

/**
 * Format activity event for display
 * @param {Object} activity - Activity record from database
 * @returns {Object} Formatted activity object
 */
export function formatActivityEvent(activity) {
  const { event_type, from_wallet, to_wallet, sol_amount, time_ago, signature } = activity;

  // Get Solana Explorer URL
  const getExplorerUrl = () => {
    const cluster = 'devnet'; // Default to devnet for now
    return `https://explorer.solana.com/tx/${signature}?cluster=${cluster}`;
  };

  // Format wallet addresses
  const formatWallet = (wallet) => {
    if (!wallet) return '';
    return `${wallet.slice(0, 4)}...${wallet.slice(-4)}`;
  };

  // Get event description
  const getEventDescription = () => {
    switch (event_type) {
      case 'mint':
        return `Minted to ${formatWallet(to_wallet)}`;
      case 'transfer':
        return `Transferred from ${formatWallet(from_wallet)} to ${formatWallet(to_wallet)}`;
      case 'sale':
        return `Sold from ${formatWallet(from_wallet)} to ${formatWallet(to_wallet)} for ${sol_amount.toFixed(4)} SOL`;
      case 'burn':
        return `Burned by ${formatWallet(from_wallet)}`;
      case 'list':
        return `Listed for sale by ${formatWallet(from_wallet)}`;
      case 'bid':
        return `Bid placed by ${formatWallet(from_wallet)}`;
      case 'cancel':
        return `Listing cancelled by ${formatWallet(from_wallet)}`;
      default:
        return 'Unknown event';
    }
  };

  // Get event icon
  const getEventIcon = () => {
    switch (event_type) {
      case 'mint':
        return '✨';
      case 'transfer':
        return '🔄';
      case 'sale':
        return '💰';
      case 'burn':
        return '🔥';
      case 'list':
        return '📋';
      case 'bid':
        return '🎯';
      case 'cancel':
        return '❌';
      default:
        return '📄';
    }
  };

  // Get event color
  const getEventColor = () => {
    switch (event_type) {
      case 'mint':
        return 'text-green-400';
      case 'transfer':
        return 'text-blue-400';
      case 'sale':
        return 'text-yellow-400';
      case 'burn':
        return 'text-red-400';
      case 'list':
        return 'text-purple-400';
      case 'bid':
        return 'text-orange-400';
      case 'cancel':
        return 'text-gray-400';
      default:
        return 'text-gray-400';
    }
  };

  return {
    ...activity,
    explorerUrl: getExplorerUrl(),
    description: getEventDescription(),
    icon: getEventIcon(),
    color: getEventColor(),
    formattedFrom: formatWallet(from_wallet),
    formattedTo: formatWallet(to_wallet)
  };
}

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} Success status
 */
export async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const result = document.execCommand('copy');
      textArea.remove();
      return result;
    }
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
}

/**
 * Get USD value of SOL amount
 * @param {number} solAmount - Amount in SOL
 * @returns {Promise<string>} Formatted USD string
 */
export async function getSOLUSDValue(solAmount) {
  try {
    // Try CoinGecko first (most reliable, works globally)
    try {
      const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd', {
        signal: AbortSignal.timeout(8000)
      });
      if (response.ok) {
        const data = await response.json();
        if (data.solana?.usd) {
          const usdValue = solAmount * data.solana.usd;
          return `$${usdValue.toFixed(2)} USD`;
        }
      }
    } catch (cgError) {
      // Continue to next fallback
    }

    // Fallback 1: CryptoCompare
    try {
      const response = await fetch('https://min-api.cryptocompare.com/data/price?fsym=SOL&tsyms=USD', {
        signal: AbortSignal.timeout(8000)
      });
      if (response.ok) {
        const data = await response.json();
        if (data.USD) {
          const usdValue = solAmount * data.USD;
          return `$${usdValue.toFixed(2)} USD`;
        }
      }
    } catch (ccError) {
      // Continue to next fallback
    }
    
    // Fallback 2: Coinbase
    try {
      const response = await fetch('https://api.coinbase.com/v2/prices/SOL-USD/spot', {
        signal: AbortSignal.timeout(8000)
      });
      if (response.ok) {
        const data = await response.json();
        if (data.data?.amount) {
          const usdValue = solAmount * parseFloat(data.data.amount);
          return `$${usdValue.toFixed(2)} USD`;
        }
      }
    } catch (cbError) {
      // Continue to next fallback
    }
    
    // Fallback 3: Kraken
    try {
      const response = await fetch('https://api.kraken.com/0/public/Ticker?pair=SOLUSD', {
        signal: AbortSignal.timeout(8000)
      });
      if (response.ok) {
        const data = await response.json();
        if (data.result?.SOLUSD?.c?.[0]) {
          const usdValue = solAmount * parseFloat(data.result.SOLUSD.c[0]);
          return `$${usdValue.toFixed(2)} USD`;
        }
      }
    } catch (krakenError) {
      // Continue to fallback
    }
    
    // Fallback to static rate if all APIs fail
    const SOL_USD_RATE = 150; // Approximate SOL price fallback
    const usdValue = solAmount * SOL_USD_RATE;
    return `~$${usdValue.toFixed(2)} USD`;
  } catch (error) {
    console.error('Error getting SOL USD value:', error);
    // Fallback to static rate
    const SOL_USD_RATE = 150; // Approximate SOL price fallback
    const usdValue = solAmount * SOL_USD_RATE;
    return `~$${usdValue.toFixed(2)} USD`;
  }
}
